"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PageStructureIndex = void 0;
exports.pageStructureIndexOf = pageStructureIndexOf;
const page_document_maps_1 = require("./page-document-maps");
const page_layer_index_1 = require("./page-layer-index");
const page_structure_ancestors_1 = require("./page-structure-ancestors");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_tree_1 = require("./page-structure-tree");
// Code unit order like derivePageTree: fractional indexes are ASCII.
const compare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const sameEntry = (a, b) => a.k === b.k &&
    a.p === b.p &&
    a.o === b.o &&
    a.s === b.s &&
    a.g === b.g &&
    a.pi === b.pi &&
    a.l === b.l &&
    a.d === b.d &&
    (0, page_structure_entry_1.samePageConnectorEnds)(a.c, b.c);
const NO_CONNECTORS = new Set();
// The ends a live connector entry is attached to, once each: both ends of
// a connector may be attached to the same element.
const connectorEnds = (entry) => entry?.c && entry.p !== page_structure_tree_1.PAGE_TREE_DELETED_ID
    ? [...new Set(entry.c.filter((end) => end !== null))]
    : [];
/**
 * The structure of one loaded page document, kept current instead of decoded
 * per read: its entries, the children of each parent by (o, id) and the
 * derived tree, derived again only after a change. Writes of the structure
 * writer reach it at once; writes of other writers and devices reach it at
 * the end of their transaction once it is subscribed to.
 */
class PageStructureIndex {
    constructor(structure, options = {}) {
        this.structure = structure;
        this.options = options;
        this.entriesById = new Map();
        /** Stored child blocks by parent id, sorted by (o, id), before tree repair. */
        this.childrenByParent = new Map();
        /** Stored canvas elements by surface id, sorted by (o, id). */
        this.elementsByParent = new Map();
        /** Live connectors by the id of each end they are attached to. */
        this.connectorsByEnd = new Map();
        /** Entries by their owning part, including entries whose fields are unloaded. */
        this.idsByPart = new Map();
        this.derived = null;
        /** Lists of the derived tree written since it was last handed out. */
        this.ownLists = new WeakSet();
        /**
         * The derived tree needed no repair (no recovered entry, no element off its
         * surface, no dropped or cut group): a write that keeps it so is applied to
         * it in place instead of deriving it again.
         */
        this.derivedClean = false;
        this.revisionValue = 0;
        this.writeListeners = new Set();
        /** Canvas layer bounds of the entries; null until read again. */
        this.layerBounds = null;
        this.listeners = new Set();
        this.pending = {
            ids: new Set(),
            parents: new Set(),
            groups: new Set(),
            reordered: new Set(),
            presented: new Set(),
        };
        this.byOrder = (a, b) => compare(this.entriesById.get(a).o, this.entriesById.get(b).o) ||
            compare(a, b);
        this.boundParts = new Map();
        this.bindPart(page_document_maps_1.PAGE_DOCUMENT_PART_ID, structure);
    }
    /** Binds all entries of a loaded part and announces them together. */
    bindPart(partId, structure) {
        const previous = this.boundParts.get(partId);
        if (previous?.structure === structure)
            return;
        if (previous)
            throw new Error(`Page part ${partId} is already bound`);
        const entries = [...structure].map(([id, value]) => [id, (0, page_structure_entry_1.decodePageStructureEntry)(value, partId)]);
        const observer = (event) => this.observePart(partId, structure, event);
        this.boundParts.set(partId, { structure, observer });
        if (this.listeners.size)
            structure.observe(observer);
        for (const [id, entry] of entries)
            this.written(id, entry);
        this.announce(false);
    }
    /** Unloading removes this part from the view, without deleting stored entries. */
    unbindPart(partId) {
        const part = this.boundParts.get(partId);
        if (!part)
            return;
        if (this.listeners.size)
            part.structure.unobserve(part.observer);
        this.boundParts.delete(partId);
        for (const id of this.idsInPart(partId))
            this.written(id, undefined);
        this.announce(false);
    }
    entries() {
        return this.entriesById;
    }
    /** Stored child blocks of a parent by (o, id); the tree repairs cycles. */
    childrenOf(parentId) {
        return this.childrenByParent.get(parentId) ?? [];
    }
    /** Stored canvas elements of a surface by (o, id): its layer order. */
    elementsOf(surfaceId) {
        return this.elementsByParent.get(surfaceId) ?? [];
    }
    /** Live connectors attached to block or element `id`, loaded or not. */
    connectorsOf(id) {
        return this.connectorsByEnd.get(id) ?? NO_CONNECTORS;
    }
    /** Structure entry IDs in one part, without scanning the whole page. */
    idsInPart(partId) {
        return this.idsByPart.get(partId) ?? NO_CONNECTORS;
    }
    attachPart(id, partId) {
        let ids = this.idsByPart.get(partId);
        if (!ids)
            this.idsByPart.set(partId, (ids = new Set()));
        ids.add(id);
    }
    /**
     * The derived tree. The same object stays current across writes that need
     * no repair; `revision` tells readers that it changed.
     */
    tree() {
        if (!this.derived) {
            this.derived = (0, page_structure_tree_1.derivePageTree)(this.entriesById, undefined, this.options.unloaded);
            this.derivedClean = this.isClean(this.derived);
        }
        // Its lists are handed out: the next write of each copies it once.
        this.ownLists = new WeakSet();
        return this.derived;
    }
    /** Counts the writes of the structure: it changes whenever the tree does. */
    get revision() {
        return this.revisionValue;
    }
    /** Calls `listener` with the id of every written entry, at once. */
    observeWrites(listener) {
        this.writeListeners.add(listener);
        return () => this.writeListeners.delete(listener);
    }
    /** First and last canvas layer of the page, as pageCanvasLayerBounds. */
    canvasLayerBounds() {
        this.layerBounds ??= (0, page_layer_index_1.pageCanvasLayerBounds)(this.entriesById);
        return this.layerBounds;
    }
    /**
     * Loaded fields appeared or disappeared without a structure write: `local`
     * when this device wrote them, not when a part loaded, unloaded or synced.
     */
    fieldsChanged(ids, local) {
        // The unfiltered tree depends on entries, never on loaded field maps.
        const changed = new Set(ids);
        if (changed.size === 0 || this.listeners.size === 0)
            return;
        const parents = new Set();
        for (const id of changed) {
            const parent = this.entriesById.get(id)?.p;
            if (parent != null)
                parents.add(parent);
        }
        const change = {
            ids: changed,
            parents,
            groups: new Set(),
            reordered: new Set(),
            presented: new Set(),
            local,
        };
        for (const listener of this.listeners)
            listener(change);
    }
    /** A write of the structure writer, applied before its transaction ends. */
    written(id, entry) {
        const previous = this.entriesById.get(id);
        if (previous === entry)
            return;
        if (previous && entry && sameEntry(previous, entry))
            return;
        if (previous?.s !== entry?.s) {
            if (previous) {
                const ids = this.idsByPart.get(previous.s);
                ids.delete(id);
                if (ids.size === 0)
                    this.idsByPart.delete(previous.s);
            }
            if (entry)
                this.attachPart(id, entry.s);
        }
        if (previous?.p != null) {
            const lists = this.listsOf(previous);
            const list = lists.get(previous.p);
            list.splice(list.indexOf(id), 1);
            if (list.length === 0)
                lists.delete(previous.p);
        }
        for (const end of connectorEnds(previous)) {
            const connectors = this.connectorsByEnd.get(end);
            connectors.delete(id);
            if (connectors.size === 0)
                this.connectorsByEnd.delete(end);
        }
        if (entry) {
            this.entriesById.set(id, entry);
            if (entry.p !== null)
                this.insertChild(entry, id);
            this.attachConnector(id, entry);
        }
        else {
            this.entriesById.delete(id);
        }
        if (!this.derivedClean || !this.applyToTree(id, previous, entry))
            this.derived = null;
        this.revisionValue += 1;
        for (const listener of this.writeListeners)
            listener(id);
        this.layerBounds = this.nextLayerBounds(id, previous, entry);
        if (this.listeners.size > 0) {
            const { pending } = this;
            pending.ids.add(id);
            if (previous?.p != null)
                pending.parents.add(previous.p);
            if (entry?.p != null)
                pending.parents.add(entry.p);
            if (previous?.g !== entry?.g) {
                if (previous?.g !== undefined)
                    pending.groups.add(previous.g);
                if (entry?.g !== undefined)
                    pending.groups.add(entry.g);
            }
            if (previous?.o !== entry?.o)
                pending.reordered.add(id);
            if (previous?.pi !== entry?.pi)
                pending.presented.add(id);
        }
    }
    /**
     * Calls `listener` after every transaction that changed the structure, on
     * this device or another. Returns the function that ends the subscription.
     */
    subscribe(listener) {
        if (this.listeners.size === 0)
            for (const part of this.boundParts.values())
                part.structure.observe(part.observer);
        this.listeners.add(listener);
        return () => {
            if (!this.listeners.delete(listener))
                return;
            if (this.listeners.size === 0)
                for (const part of this.boundParts.values())
                    part.structure.unobserve(part.observer);
        };
    }
    observePart(partId, structure, event) {
        for (const id of event.keysChanged) {
            const value = structure.get(id);
            if (value === undefined) {
                // A removal only ends the entry this part holds: a move to another
                // part writes there first and removes here when its own write ends.
                if (this.entriesById.get(id)?.s === partId)
                    this.written(id, undefined);
                continue;
            }
            this.written(id, (0, page_structure_entry_1.decodePageStructureEntry)(value, partId));
        }
        this.announce(event.transaction.local);
    }
    announce(local) {
        const { pending } = this;
        const change = {
            ids: new Set(pending.ids),
            parents: new Set(pending.parents),
            groups: new Set(pending.groups),
            reordered: new Set(pending.reordered),
            presented: new Set(pending.presented),
            local,
        };
        Object.values(pending).forEach(set => set.clear());
        if (change.ids.size === 0)
            return;
        for (const listener of this.listeners)
            listener(change);
    }
    // A new entry without children only widens the bounds, as bulk inserts
    // write; any other write reads them again from all entries.
    nextLayerBounds(id, previous, entry) {
        const bounds = this.layerBounds;
        if (!bounds ||
            previous ||
            !entry ||
            this.childrenByParent.has(id) ||
            this.elementsByParent.has(id))
            return null;
        const layer = (0, page_layer_index_1.pageEntryCanvasLayer)(id, entry, this.entriesById);
        if (layer instanceof page_layer_index_1.PageLayerIndexError)
            return null;
        if (layer === null)
            return bounds;
        return {
            first: bounds.first === null || layer < bounds.first ? layer : bounds.first,
            last: bounds.last === null || layer > bounds.last ? layer : bounds.last,
        };
    }
    live(id) {
        const entry = this.entriesById.get(id);
        return entry && (0, page_structure_tree_1.isPageTreeEntry)(entry) ? entry : undefined;
    }
    isClean(tree) {
        if (tree.recovered.length > 0)
            return false;
        // One pass over the element lists: a lookup per element would walk the
        // list of its surface, which holds most elements of a canvas page.
        const surfaceOf = new Map();
        for (const [surface, ids] of tree.elements)
            for (const id of ids)
                surfaceOf.set(id, surface);
        for (const [id, entry] of this.entriesById) {
            if (!(0, page_structure_tree_1.isPageTreeEntry)(entry))
                continue;
            if (id !== tree.root && !tree.parentOf.has(id) && !surfaceOf.has(id)) {
                let omitted = false;
                for (const [parent, ancestor] of (0, page_structure_ancestors_1.pageStructureAncestors)(this.entriesById, entry.p)) {
                    if (ancestor?.p === page_structure_tree_1.PAGE_TREE_DELETED_ID ||
                        (!ancestor && this.options.unloaded?.(parent))) {
                        omitted = true;
                        break;
                    }
                }
                if (omitted)
                    continue;
            }
            if ((0, page_structure_entry_1.isPageElementKind)(entry.k)) {
                if (surfaceOf.get(id) !== (entry.p ?? ''))
                    return false;
            }
            else if (id !== tree.root && tree.parentOf.get(id) !== entry.p) {
                return false;
            }
            if (entry.g !== undefined &&
                entry.g !== id &&
                tree.groupOf.get(id) !== entry.g)
                return false;
        }
        return true;
    }
    /**
     * Applies the write of `id` to the clean derived tree, as derivePageTree
     * would place it; false when the write needs a repair (an unknown or
     * removed parent, surface or group, a cycle, a second root), which only a
     * new derivation decides.
     */
    applyToTree(id, previous, entry) {
        const tree = this.derived;
        if (!tree)
            return true;
        const was = previous && (0, page_structure_tree_1.isPageTreeEntry)(previous) ? previous : undefined;
        if (was &&
            !(0, page_structure_entry_1.isPageElementKind)(was.k) &&
            id !== tree.root &&
            !tree.parentOf.has(id))
            return false;
        if (!was && this.childrenByParent.get(id)?.length)
            return false;
        const is = entry && (0, page_structure_tree_1.isPageTreeEntry)(entry) ? entry : undefined;
        if (was && is && (0, page_structure_entry_1.isPageElementKind)(was.k) !== (0, page_structure_entry_1.isPageElementKind)(is.k))
            return false;
        const element = (0, page_structure_entry_1.isPageElementKind)((is ?? was)?.k ?? '');
        const children = tree.children;
        const elements = tree.elements;
        const members = tree.members;
        const parentOf = tree.parentOf;
        const groupOf = tree.groupOf;
        // Whatever stood on or in a removed entry needs a repair.
        if (was &&
            !is &&
            (children.get(id)?.length ||
                elements.get(id)?.length ||
                members.get(id)?.length))
            return false;
        // The new place must exist without repair.
        if (is) {
            if (is.p === null) {
                if (element || id !== tree.root)
                    return false;
            }
            else {
                const parent = this.live(is.p);
                if (!parent ||
                    is.p === id ||
                    (0, page_structure_entry_1.isPageElementKind)(parent.k) ||
                    (is.p !== tree.root && !parentOf.has(is.p)))
                    return false;
                if (!element)
                    for (let at = is.p; at !== undefined;) {
                        if (at === id)
                            return false;
                        at = parentOf.get(at);
                    }
            }
            if (is.g !== undefined && is.g !== id) {
                if (!this.live(is.g))
                    return false;
                for (let at = is.g; at !== undefined;) {
                    if (at === id)
                        return false;
                    at = groupOf.get(at);
                }
            }
        }
        else if (id === tree.root) {
            return false;
        }
        if (was && id === tree.root && is?.p !== null)
            return false;
        // Leave the old place, take the new one in (o, id) order.
        const lists = element ? elements : children;
        if (was?.p != null) {
            this.removeFrom(lists, was.p, id);
            if (!element)
                parentOf.delete(id);
        }
        const group = groupOf.get(id);
        if (group !== undefined) {
            this.removeFrom(members, group, id);
            groupOf.delete(id);
        }
        // A list handed out stays as it was: a changed one is replaced.
        let presentation = tree.presentation;
        if (was?.pi !== undefined)
            presentation = presentation.filter(other => other !== id);
        tree.presentation = presentation;
        if (!is)
            return true;
        if (is.p !== null) {
            this.insertSorted(lists, is.p, id);
            if (!element)
                parentOf.set(id, is.p);
        }
        if (is.g !== undefined && is.g !== id) {
            this.insertSorted(members, is.g, id);
            groupOf.set(id, is.g);
        }
        if (is.pi !== undefined) {
            const pi = is.pi;
            const at = presentation.findIndex(other => {
                const otherPi = this.entriesById.get(other).pi;
                return compare(pi, otherPi) < 0 || (pi === otherPi && id < other);
            });
            const next = [...presentation];
            next.splice(at === -1 ? next.length : at, 0, id);
            tree.presentation = next;
        }
        return true;
    }
    // A list handed out stays as it was: its first write after that replaces
    // it with a copy, which later writes change in place.
    ownList(lists, key) {
        const list = lists.get(key);
        if (list && this.ownLists.has(list))
            return list;
        const own = [...(list ?? [])];
        lists.set(key, own);
        this.ownLists.add(own);
        return own;
    }
    removeFrom(lists, key, id) {
        const at = lists.get(key)?.indexOf(id) ?? -1;
        if (at === -1)
            return;
        const list = this.ownList(lists, key);
        list.splice(at, 1);
        if (list.length === 0)
            lists.delete(key);
    }
    insertSorted(lists, key, id) {
        const list = this.ownList(lists, key);
        let low = 0;
        let high = list.length;
        while (low < high) {
            const middle = (low + high) >>> 1;
            if (this.byOrder(list[middle], id) < 0)
                low = middle + 1;
            else
                high = middle;
        }
        list.splice(low, 0, id);
    }
    attachConnector(id, entry) {
        for (const end of connectorEnds(entry)) {
            let connectors = this.connectorsByEnd.get(end);
            if (!connectors) {
                connectors = new Set();
                this.connectorsByEnd.set(end, connectors);
            }
            connectors.add(id);
        }
    }
    listsOf(entry) {
        return (0, page_structure_entry_1.isPageElementKind)(entry.k)
            ? this.elementsByParent
            : this.childrenByParent;
    }
    /** The list of the entry's parent; `p` is set. */
    listOf(entry) {
        const lists = this.listsOf(entry);
        let list = lists.get(entry.p);
        if (!list) {
            list = [];
            lists.set(entry.p, list);
        }
        return list;
    }
    insertChild(entry, id) {
        const list = this.listOf(entry);
        let low = 0;
        let high = list.length;
        while (low < high) {
            const middle = (low + high) >>> 1;
            if (this.byOrder(list[middle], id) < 0)
                low = middle + 1;
            else
                high = middle;
        }
        list.splice(low, 0, id);
    }
}
exports.PageStructureIndex = PageStructureIndex;
const sharedIndexes = new WeakMap();
/**
 * The index of a structure map that its writers and readers share: built
 * once per map and subscribed, so it sees the writes of other writers and
 * devices at the end of their transaction. The subscription ends when the
 * document is destroyed.
 */
function pageStructureIndexOf(structure, options = {}) {
    const shared = sharedIndexes.get(structure);
    if (shared) {
        if (options.unloaded && shared.unloaded !== options.unloaded)
            throw new Error('Page structure index has another unloaded rule');
        return shared.index;
    }
    const doc = structure.doc;
    if (!doc)
        throw new Error('A page structure map outside of a document has no index');
    const index = new PageStructureIndex(structure, options);
    const release = index.subscribe(() => { });
    doc.once('destroy', () => {
        release();
        sharedIndexes.delete(structure);
    });
    sharedIndexes.set(structure, { index, unloaded: options.unloaded });
    return index;
}
